import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Icon, Typography } from '@ann/web-components';
import DashboardCardMenu from '../DashboardCardMenu';
import timeAgo from '../../../../../utils/timeAgo';
import { MAX_VISIBLE_TAGS, DASHBOARD_STATES } from '../../constants';
import { DASHBOARD_LIST_COPY } from '../../constants/dashboardBuilderCopy';
import {
  CardContainer,
  TitleRow,
  TitleLeft,
  TitleTextWrap,
  SeparatorDot,
  SectionPath,
  SectionPathText,
  TitleRight,
  DescriptionBox,
  DescriptionText,
  MetadataRow,
  MetaPill,
  MetaSeparator,
  MetaGroup,
  MetaText,
  TagsArea,
  TagTooltipTrigger,
} from './DashboardCard.styled';

const DashboardCard = ({
  dashboard,
  onEdit,
  onShare,
  onArchive,
  onDuplicate,
}) => {
  const navigate = useNavigate();
  const { stateOptions } = DASHBOARD_LIST_COPY.filters;

  const { visibleTags, overflowTags, overflowCount } = useMemo(() => {
    const tags = dashboard.tags || [];
    return {
      visibleTags: tags.slice(0, MAX_VISIBLE_TAGS),
      overflowTags: tags.slice(MAX_VISIBLE_TAGS),
      overflowCount: Math.max(0, tags.length - MAX_VISIBLE_TAGS),
    };
  }, [dashboard.tags]);

  const handleClick = () => {
    navigate(`/dashboards/${dashboard.id}`);
  };

  const handleEdit = () => {
    navigate(`/dashboards/${dashboard.id}/edit`);
  };

  const handleShare = () => {
    onShare?.(dashboard.id);
  };

  const handleArchive = () => {
    onArchive?.(dashboard.id);
  };

  const handleDuplicate = () => {
    onDuplicate?.(dashboard.id);
  };

  const statusLabel = useMemo(() => {
    if (dashboard.state === DASHBOARD_STATES.PUBLISHED) return stateOptions.published;
    if (dashboard.state === DASHBOARD_STATES.ARCHIVED) return stateOptions.archived;
    return stateOptions.draft;
  }, [dashboard.state, stateOptions.archived, stateOptions.draft, stateOptions.published]);

  const updatedAtLabel = timeAgo(dashboard.updatedAt) ?? '—';

  return (
    <CardContainer onClick={handleClick}>
      <TitleRow>
        <TitleLeft>
          <TitleTextWrap title={dashboard.title}>
            <Typography category="subtitle" weight="bold" size="medium" color="subtitle" titleOnHover>
              {dashboard.title || 'Untitled Dashboard'}
            </Typography>
          </TitleTextWrap>
          {dashboard.sectionPath && (
            <>
              <SeparatorDot>·</SeparatorDot>
              <SectionPath>
                <Icon name="locationOn" size="small" color="neutral" />
                <SectionPathText title={dashboard.sectionPath}>
                  {dashboard.sectionPath}
                </SectionPathText>
              </SectionPath>
            </>
          )}
        </TitleLeft>
        <TitleRight>
          <Tag
            text={statusLabel}
            color={dashboard.state === DASHBOARD_STATES.PUBLISHED ? 'success' : 'neutral'}
            icon={dashboard.state === DASHBOARD_STATES.PUBLISHED ? 'check' : undefined}
            size="small"
          />
          <DashboardCardMenu
            onEdit={handleEdit}
            onShare={handleShare}
            onArchive={handleArchive}
            onDuplicate={handleDuplicate}
            isArchived={dashboard.state === DASHBOARD_STATES.ARCHIVED}
          />
        </TitleRight>
      </TitleRow>

      {dashboard.description && (
        <DescriptionBox>
          <DescriptionText title={dashboard.description}>
            {dashboard.description}
          </DescriptionText>
        </DescriptionBox>
      )}

      <MetadataRow>
        <MetaPill>
          <MetaGroup>
            <MetaText>{dashboard.creatorName || '—'}</MetaText>
          </MetaGroup>
          <MetaSeparator>•</MetaSeparator>
          <MetaGroup>
            <Icon name="schedule" size="small" color="neutral" />
            <MetaText>{updatedAtLabel}</MetaText>
          </MetaGroup>
          <MetaSeparator>•</MetaSeparator>
          <MetaGroup>
            <Icon materialName="widgets" size="small" color="neutral" />
            <MetaText>{dashboard.widgetCount ?? 0}</MetaText>
          </MetaGroup>
        </MetaPill>
        <TagsArea>
          {visibleTags.map((tag) => (
            <Tag
              key={tag}
              text={tag}
              color="lightBlue"
              size="small"
            />
          ))}
          {overflowCount > 0 && (
            <TagTooltipTrigger>
              <Tag
                text={`+${overflowCount}`}
                color="lightBlue"
                size="small"
                tooltip={overflowTags.join(', ')}
              />
            </TagTooltipTrigger>
          )}
        </TagsArea>
      </MetadataRow>
    </CardContainer>
  );
};

export default DashboardCard;
